import { useState } from "react";
import { Link } from "react-router-dom";
import { Scale, AlertTriangle, CheckCircle2, X, Clock, DollarSign, ShieldCheck, FileText, Info, ChevronDown, ArrowRight, Gavel, HeartHandshake, GraduationCap } from "lucide-react";
import { Disclaimer, CTASection } from "@/components/Shared";

const SNAPSHOT = [
  { k: "Review body", v: "Administrative Review Tribunal (ART)" },
  { k: "Replaced", v: "The AAT on 14 October 2024" },
  { k: "Review type", v: "Merits review — fresh evidence & fresh arguments allowed" },
  { k: "Key reform date", v: "18 May 2026 — paper-based review changes commenced" },
  { k: "Practice directions", v: "New migration, protection & character directions from 2 March 2026" },
  { k: "Typical deadlines", v: "14 days (detainees) · 21 days (most onshore) · 28 days (certain offshore)" },
  { k: "Student visa impact", v: "Subclass 500 refusal reviews now generally decided on the papers" },
  { k: "Fee from 1 July 2026", v: "AUD $3,727 for most reviewable migration decisions" },
];

const DEADLINE_TABLE = [
  { cat: "Onshore detainees", d: "14 calendar days", m: "Extremely short review window — urgent filing and evidence planning are essential" },
  { cat: "Most onshore visa refusals", d: "21 calendar days", m: "The most common 2026 deadline, but it still depends on subclass and notification method" },
  { cat: "Certain offshore and other matters", d: "28 calendar days", m: "Often assumed as the default rule, but it does not apply to every category" },
];

const CAN_APPLY = [
  "Partner and family visa refusals — including Subclass 820, 801, 309 and 100, plus some Visitor Visa refusals",
  "Skilled migration refusals — including Subclass 189, 190 and 491",
  "Employer-sponsored refusals — including Subclass 482 and 494 in relevant circumstances",
  "Student visa refusals (Subclass 500) — now affected by the paper-based process from 18 May 2026",
];

const NOT_REVIEWABLE = [
  "Adverse ASIO security assessment cases",
  "Ministerial personal decisions under sections 501A or 501BA",
  "Many offshore decisions with no sponsor or eligible review pathway in Australia",
];

const PROCESS_STEPS = [
  { t: "Lodgement", d: "The review application is filed through the ART online portal within time, and the fee is paid. The portal timestamps the application to the minute — the review does not commence until payment is made." },
  { t: "Written submissions & evidence", d: "Now the most important stage. Material must address EACH refusal reason — updated documents, statutory declarations, expert evidence where relevant, and a structured response to the Department's concerns." },
  { t: "Hearing or paper determination", d: "Many categories still proceed to a hearing (in person, phone or video). Student visa refusals from 18 May 2026 onward are generally decided on the papers without an oral hearing." },
  { t: "Decision", d: "The ART may set aside the refusal, affirm it, or remit the matter to the Department for reconsideration. If affirmed, the next pathway may be judicial review in the Federal Circuit and Family Court." },
];

const REFUSAL_GROUNDS = [
  { g: "GTE / GS concerns", says: "The applicant does not appear to be a genuine temporary entrant or genuine student", must: "Rebuild the narrative with education, travel, financial and future-plan evidence that directly answers the stated concerns" },
  { g: "Financial evidence issues", says: "Funds are insufficient, inconsistent, unexplained or unsupported", must: "Provide updated bank evidence, funding sources, declarations and a clean document trail" },
  { g: "PIC 4020", says: "A bogus document or false material statement is alleged", must: "Address authenticity, knowledge, materiality and the consequences of a three-year exclusion period" },
  { g: "Section 48 bar", says: "The applicant is onshore after a refusal and cannot lodge most new visa applications", must: "Assess whether review is still open, what visa options remain, and how status is protected in the meantime" },
  { g: "Section 501 character issues", says: "The applicant is considered a risk because of criminal history or conduct", must: "Separate delegate decisions from non-reviewable ministerial decisions and prepare a high-evidence response on discretion and risk" },
];

const FEES = [
  { type: "Reviewable migration decision", before: "$3,580", after: "$3,727", hot: true },
  { type: "Protection decision review", before: "$2,203", after: "$2,293" },
  { type: "Standard application fee", before: "$1,148", after: "$1,195" },
  { type: "Guidance and Appeals Panel review", before: "$1,148", after: "$1,195" },
  { type: "Concessional circumstances fee", before: "$100", after: "$100 (unchanged)", same: true },
];

const PAYMENT_TIMING = [
  { s: "Application lodged AND fee paid before 1 July 2026", i: "Current (old) fee may apply" },
  { s: "Application lodged before 1 July but fee paid on or after 1 July 2026", i: "New fee may apply" },
  { s: "Application lodged and paid on or after 1 July 2026", i: "New fee applies" },
  { s: "Waiting until the last day without preparing payment", i: "Risk of paying the higher fee — or missing the deadline entirely" },
];

const DEADLINES = [
  { k: "Decision letter date", v: "Usually the starting point for checking review rights and your deadline" },
  { k: "Reviewable decision", v: "Not every decision has the same review option — check your letter" },
  { k: "Time limit", v: "Must be checked from the refusal or cancellation letter itself" },
  { k: "No extension power", v: "The ART cannot extend the time limit — no exceptions" },
  { k: "Late action", v: "May result in permanently losing your review rights" },
];

const HARDSHIP = [
  { k: "Financial hardship", v: "A 50% fee reduction may be available for migration reviews" },
  { k: "Not automatic", v: "You must request the reduction and support the claim" },
  { k: "Evidence needed", v: "Your financial circumstances may need to be shown" },
  { k: "Timing matters", v: "The fee and reduction request must be handled before the deadline" },
  { k: "Concessional fee", v: "The fee in concessional circumstances remains AUD $100" },
];

const STUDENT_REVIEW = [
  { k: "Written submissions", v: "May become the main (or only) way to explain your case" },
  { k: "Refusal reasons", v: "Must be answered clearly, point by point" },
  { k: "Supporting documents", v: "Should directly address the Tribunal's concerns" },
  { k: "Timeline", v: "The strict deadline still applies" },
  { k: "Fee increase", v: "Adds extra cost pressure from 1 July 2026" },
];

const REVIEW_TYPES = [
  { t: "Partner visa refusal", w: "Relationship evidence may need detailed preparation" },
  { t: "Student visa refusal", w: "Written submissions and study intention evidence may be critical" },
  { t: "Visitor visa refusal", w: "Genuine visitor and incentive-to-return evidence may matter" },
  { t: "Temporary work visa refusal", w: "Role, skills and sponsor-related evidence may be reviewed" },
  { t: "Nomination or sponsor refusal", w: "Employer documents may need careful checking" },
  { t: "Family visa refusal", w: "Relationship, dependency or eligibility evidence may be important" },
];

const APPROACH = [
  { poor: "Lodging quickly without reading the refusal reasons", strong: "Reviewing the exact decision letter carefully" },
  { poor: "Uploading random documents", strong: "Providing documents that answer the refusal issues" },
  { poor: "Assuming a hearing will fix the case", strong: "Preparing strong written material early" },
  { poor: "Missing payment or deadline details", strong: "Confirming fee, deadline and review rights immediately" },
  { poor: "Treating all refusals the same", strong: "Building a case-specific review strategy" },
];

const PROCESSING = [
  { cat: "All migration case categories", half: "1 year 7 months", most: "2 years 10 months" },
  { cat: "Student refusal", half: "1 year 7 months", most: "2 years" },
  { cat: "Student cancellation", half: "1 year 3 months", most: "1 year 11 months" },
  { cat: "Skill linked", half: "1 year 4 months", most: "2 years 9 months" },
  { cat: "Nomination / Sponsor approval", half: "1 year 7 months", most: "2 years 5 months" },
  { cat: "Partner", half: "3 years 2 months", most: "3 years 10 months" },
  { cat: "Visitor", half: "10 months", most: "2 years 1 month" },
];

const CHECKLIST = [
  ["Read the decision letter", "Confirm the refusal reasons and your review rights"],
  ["Check the deadline", "ART deadlines are strict and cannot be extended"],
  ["Confirm the fee", "Check whether the old or new fee applies based on your payment date"],
  ["Review fee reduction options", "Consider the financial hardship reduction if eligible"],
  ["Prepare documents", "Evidence should answer the refusal reasons — not just repeat your application"],
  ["Write submissions", "Explain your case clearly and logically"],
  ["Keep proof of lodgement", "Save your confirmation and payment records"],
  ["Continue updating evidence", "Upload relevant documents during the review"],
];

const SITUATIONS = [
  { s: "Student visa refused", r: "Study history, genuine study reasons and refusal concerns" },
  { s: "Partner visa refused", r: "Relationship evidence and the decision findings" },
  { s: "Visitor visa refused", r: "Purpose of visit and incentives to return home" },
  { s: "Employer nomination refused", r: "Business, role and nomination evidence" },
  { s: "Temporary work visa refused", r: "Skills, occupation and employer documents" },
  { s: "Protection refusal", r: "Case evidence and the specific review process that applies" },
];

const FAQS = [
  { q: "How much is the ART migration review fee from 1 July 2026?", a: "From 1 July 2026, the fee for review of a reviewable migration decision increases to AUD $3,727 (up from $3,580)." },
  { q: "What was the ART migration review fee before 1 July 2026?", a: "Before the increase, the application fee for most migration decisions was AUD $3,580." },
  { q: "Does the new fee apply if I lodged before 1 July 2026?", a: "The new fees apply to any application fee PAID on or after 1 July 2026 — even if the review application was lodged before that date. The payment date matters, not just the lodgement date." },
  { q: "Can I get a reduced ART review fee?", a: "A 50% reduction may be granted for reviewable migration decisions on financial hardship grounds. It is not automatic — you must apply and provide supporting information. The concessional circumstances fee remains AUD $100." },
  { q: "What is the protection visa review fee from 1 July 2026?", a: "The fee for review of a reviewable protection decision increases to AUD $2,293 (from $2,203). Unlike most migration reviews, this fee is generally payable only if the application is NOT successful." },
  { q: "Can the ART extend my review deadline?", a: "No. The ART has no power to extend the time limit to apply for a review. Time limits are very strict and depend on the type of decision and whether you are in immigration detention." },
  { q: "Where do I find my review deadline?", a: "Check the decision letter from the Department of Home Affairs. It explains whether review rights exist and exactly when the deadline applies. Never rely on general online information for your deadline." },
  { q: "Does every visa refusal have ART review rights?", a: "No — not every decision is reviewable in the same way. Your decision letter confirms whether review rights are available for your specific case." },
  { q: "Will my student visa refusal review have a hearing?", a: "Not always. For many student visa refusal reviews the Tribunal may now decide the matter on the written materials alone, without an oral hearing, except in limited circumstances. This makes strong written submissions critical." },
  { q: "Can you help with my ART review?", a: "Yes. As a Registered Migration Agent (MARN 2619415) and Licensed Immigration Adviser (IAA 202603837), Shamima can review your refusal decision, confirm your deadline and review rights, prepare evidence that answers the refusal reasons, and build a case-specific review strategy. Book a consultation to get started." },
];

export default function ArtReviews() {
  return (
    <div data-testid="art-reviews-page" className="bg-white">
      {/* HERO */}
      <section className="relative bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A] text-white overflow-hidden">
        <span aria-hidden className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#29324A] via-[#C43A89] to-[#C43A89]" />
        <span aria-hidden className="absolute -top-40 -right-40 w-[28rem] h-[28rem] rounded-full bg-[#C43A89]/25 blur-3xl" />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14 md:py-20 relative">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
            <Gavel className="h-3 w-3" /> Visa Refusals · ART Reviews · Updated 1 July 2026
          </span>
          <h1 className="font-display text-4xl md:text-5xl leading-[1.05] tracking-tight mt-4 max-w-3xl">
            ART Visa Refusal Reviews — fees, deadlines &amp; strategy after the 1 July 2026 update.
          </h1>
          <p className="text-[15px] text-slate-200 leading-relaxed mt-5 max-w-2xl">
            A visa refusal can affect study plans, work rights, family plans, employer sponsorship and long-term PR planning. From 1 July 2026 the Administrative Review Tribunal (ART) increased most application fees — here is everything you need to know before you lodge.
          </p>
          <div className="mt-6 bg-rose-500/15 border border-rose-300/40 p-4 max-w-2xl flex items-start gap-3" data-testid="art-hero-alert">
            <AlertTriangle className="h-5 w-5 text-[#C43A89] shrink-0 mt-0.5" />
            <p className="text-[13px] text-slate-100 leading-relaxed">
              <strong>Don&apos;t delay.</strong> ART review deadlines are strict and the Tribunal has <strong>no power to extend them</strong>. If you have received a refusal or cancellation, check your Home Affairs decision letter and act immediately.
            </p>
          </div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/book-consultation" data-testid="art-hero-book" className="inline-flex items-center gap-2 bg-[#C43A89] hover:bg-[#a22b6e] text-white text-xs font-bold tracking-[0.2em] uppercase px-6 py-4 transition-colors">
              Discuss My Refusal Now <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* WHAT IS THE ART */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#29324A] uppercase">
            <Scale className="h-3 w-3" /> Start here — what the ART actually is
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3 max-w-3xl">Your visa was refused. The clock is already running.</h2>
          <div className="mt-5 max-w-3xl space-y-4 text-[13.5px] text-slate-600 leading-relaxed">
            <p>Once the Department of Home Affairs issues a refusal, your appeal rights start expiring immediately. Some review windows are as short as <strong className="text-[#29324A]">14 days</strong>, many are <strong className="text-[#29324A]">21 days</strong>, and some are 28 days. Miss the deadline and the ART usually cannot revive the case.</p>
            <p>The <strong className="text-[#29324A]">Administrative Review Tribunal (ART)</strong> is Australia&apos;s independent federal body that reviews decisions made by Commonwealth agencies — including visa refusals and cancellations by the Department of Home Affairs. It replaced the AAT on <strong className="text-[#29324A]">14 October 2024</strong> under the Administrative Review Tribunal Act 2024.</p>
            <p>The ART performs <strong className="text-[#29324A]">merits review</strong>: the Tribunal stands in the shoes of the original decision-maker and decides the case again on its own view of the facts and the law. This is different from court-based judicial review, where the court only looks for legal error. Because it is merits review, you can provide <strong className="text-[#29324A]">new evidence</strong>, explain context missing from the original application, correct misunderstandings, and directly answer the refusal reasons.</p>
          </div>

          <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="art-snapshot">
            {SNAPSHOT.map((s) => (
              <div key={s.k} className="bg-[#F8F4FB] border border-[#29324A]/15 p-4">
                <div className="text-[9.5px] tracking-[0.18em] uppercase font-bold text-[#29324A]">{s.k}</div>
                <div className="text-[12.5px] text-[#29324A] font-medium mt-1 leading-snug">{s.v}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-[#FDF2F8] border-l-4 border-[#C43A89] p-5 max-w-3xl" data-testid="art-strategy-insight">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#C43A89]">RMA insight — don&apos;t treat a review as a delay tactic</div>
            <p className="text-[13px] text-slate-700 leading-relaxed mt-2">The biggest mistake is lodging an ART application just to buy time. A review only works when the file is <strong>rebuilt around the refusal grounds</strong> — with new evidence, a written response to every concern, and a strategy that starts the day the refusal letter arrives.</p>
          </div>

          {/* 2026 reforms */}
          <div className="mt-12">
            <h3 className="font-display text-2xl md:text-3xl text-[#29324A]">The 2026 ART reforms — and why they matter</h3>
            <div className="mt-5 grid md:grid-cols-2 gap-4">
              <div className="bg-white border border-slate-200 border-t-4 border-t-[#C43A89] p-6">
                <div className="font-display text-lg text-[#29324A]">18 May 2026 — paper-based reviews commenced</div>
                <p className="text-[12.5px] text-slate-600 leading-relaxed mt-2">Under the Administrative Review Tribunal and Other Legislation Amendment Act 2026, certain temporary visa refusal reviews can now be decided <strong>without an oral hearing</strong>. Student visa (500) refusals are the first major category affected — the applicant may never appear before a Tribunal member, speak through an interpreter, or explain nuance in real time. The same framework may be extended to other temporary visa categories by regulation.</p>
              </div>
              <div className="bg-white border border-slate-200 border-t-4 border-t-[#29324A] p-6">
                <div className="font-display text-lg text-[#29324A]">2 March 2026 — new Practice Directions</div>
                <p className="text-[12.5px] text-slate-600 leading-relaxed mt-2">New Practice Directions introduced tighter compliance expectations and a more court-like case management structure for migration, protection and character matters. Where there is no hearing, the written submission is no longer support material — <strong>it is the case itself</strong>.</p>
              </div>
            </div>
          </div>

          {/* Who can apply */}
          <div className="mt-12 grid md:grid-cols-2 gap-4" data-testid="art-who-can-apply">
            <div className="bg-[#F0FDF4] border-l-4 border-emerald-500 p-6">
              <div className="font-display text-lg text-[#29324A]">Who can usually apply for ART review</div>
              <ul className="mt-3 space-y-2">
                {CAN_APPLY.map((c) => (
                  <li key={c} className="flex gap-2 text-[12.5px] text-slate-700"><CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" /> {c}</li>
                ))}
              </ul>
            </div>
            <div className="bg-rose-50 border-l-4 border-rose-500 p-6">
              <div className="font-display text-lg text-[#29324A]">Common non-reviewable decisions</div>
              <ul className="mt-3 space-y-2">
                {NOT_REVIEWABLE.map((c) => (
                  <li key={c} className="flex gap-2 text-[12.5px] text-slate-700"><X className="h-4 w-4 text-rose-500 shrink-0 mt-0.5" /> {c}</li>
                ))}
              </ul>
              <p className="text-[11.5px] text-slate-500 mt-3">Not every refusal carries review rights — your refusal notice is the starting point, but the pathway should still be checked carefully because review rights are sometimes misdescribed.</p>
            </div>
          </div>
        </div>
      </section>

      {/* FEES */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase">
            <DollarSign className="h-3 w-3" /> 1 July 2026 fee update
          </span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3 max-w-3xl">ART review fees increased from 1 July 2026</h2>
          <p className="text-[13.5px] text-slate-600 mt-4 max-w-3xl">The biggest change for migration applicants: the fee for a <strong className="text-[#29324A]">reviewable migration decision</strong> increased from AUD $3,580 to <strong className="text-[#C43A89]">AUD $3,727</strong>. This affects refusals and cancellations involving student, partner, visitor and temporary work visas, plus sponsorship and nomination matters.</p>
          <div className="mt-8 bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]" data-testid="art-fees-table">
              <thead><tr className="bg-[#29324A] text-white">
                <th className="text-left px-4 py-4 font-display text-[13px]">Fee type</th>
                <th className="text-left px-4 py-4 font-display text-[13px]">Before 1 July 2026</th>
                <th className="text-left px-4 py-4 font-display text-[13px]">From 1 July 2026</th>
              </tr></thead>
              <tbody>
                {FEES.map((f, i) => (
                  <tr key={f.type} className={`${i % 2 ? "bg-[#FCFBFD]" : "bg-white"} border-t border-slate-100 ${f.hot ? "bg-[#FDF2F8]" : ""}`}>
                    <td className="px-4 py-4 text-[12.5px] text-[#29324A] font-semibold">{f.type}</td>
                    <td className={`px-4 py-4 text-[12.5px] ${f.same ? "text-slate-700" : "text-slate-500 line-through"}`}>AUD {f.before}</td>
                    <td className={`px-4 py-4 text-[13px] font-bold ${f.hot ? "text-[#C43A89]" : "text-[#29324A]"}`}>AUD {f.after}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 grid sm:grid-cols-2 gap-3">
            <div className="bg-[#F0FDF4] border-l-4 border-emerald-500 p-4 flex items-start gap-3" data-testid="art-fee-refund">
              <Info className="h-5 w-5 text-emerald-700 shrink-0 mt-0.5" />
              <p className="text-[12.5px] text-slate-700 leading-relaxed"><strong className="text-[#29324A]">Good to know:</strong> if the ART sets aside the Department&apos;s decision in your favour, around <strong>50% of the fee is usually refundable</strong>.</p>
            </div>
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 flex items-start gap-3">
              <AlertTriangle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" />
              <p className="text-[12.5px] text-slate-700 leading-relaxed"><strong className="text-[#29324A]">Payment first:</strong> the fee is paid on lodgement and the review <strong>does not commence until payment is made</strong>.</p>
            </div>
          </div>

          {/* Payment timing */}
          <div className="mt-10 bg-[#F8F4FB] border-l-4 border-[#29324A] p-6" data-testid="art-payment-timing">
            <h3 className="font-display text-xl text-[#29324A]">The payment date decides which fee applies — not the lodgement date</h3>
            <p className="text-[13px] text-slate-700 leading-relaxed mt-2">The ART has confirmed the new fees apply to any application fee <strong>paid on or after 1 July 2026</strong> — even if the review was lodged before that date. Lodging early does NOT automatically lock in the old fee.</p>
            <div className="mt-4 bg-white border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm min-w-[520px]">
                <thead><tr className="bg-[#29324A] text-white">
                  <th className="text-left px-4 py-3 font-display text-[12.5px]">Situation</th>
                  <th className="text-left px-4 py-3 font-display text-[12.5px]">Fee impact</th>
                </tr></thead>
                <tbody>
                  {PAYMENT_TIMING.map((r, i) => (
                    <tr key={r.s} className={`${i % 2 ? "bg-[#FCFBFD]" : "bg-white"} border-t border-slate-100`}>
                      <td className="px-4 py-3 text-[12.5px] text-[#29324A] font-medium">{r.s}</td>
                      <td className="px-4 py-3 text-[12.5px] text-slate-600">{r.i}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* DEADLINES + HARDSHIP */}
      <section className="py-14 md:py-20 bg-[#F8F9FA]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-rose-600 uppercase"><Clock className="h-3 w-3" /> The most critical part of the case</span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3 max-w-3xl">How many calendar days do you have?</h2>
          <p className="text-[13.5px] text-slate-600 mt-4 max-w-3xl">Migration review deadlines are strict, measured in <strong className="text-[#29324A]">calendar days</strong>, and in most categories the ART has <strong className="text-[#29324A]">no power to extend them</strong>. The countdown usually starts the day after notification of the refusal decision.</p>
          <div className="mt-6 bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]" data-testid="art-deadline-table">
              <thead><tr className="bg-[#29324A] text-white">
                <th className="text-left px-4 py-3.5 font-display text-[13px]">Category</th>
                <th className="text-left px-4 py-3.5 font-display text-[13px]">Typical deadline</th>
                <th className="text-left px-4 py-3.5 font-display text-[13px]">What it means in practice</th>
              </tr></thead>
              <tbody>
                {DEADLINE_TABLE.map((r, i) => (
                  <tr key={r.cat} className={`${i % 2 ? "bg-[#FCFBFD]" : "bg-white"} border-t border-slate-100`}>
                    <td className="px-4 py-3.5 text-[12.5px] text-[#29324A] font-semibold">{r.cat}</td>
                    <td className="px-4 py-3.5 text-[13px] text-[#C43A89] font-bold whitespace-nowrap">{r.d}</td>
                    <td className="px-4 py-3.5 text-[12.5px] text-slate-600">{r.m}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[13px] text-slate-600 mt-4 max-w-3xl bg-white border-l-4 border-[#29324A] p-4"><strong className="text-[#29324A]">The refusal letter is the source of truth.</strong> Do not rely on generic online summaries. The right question is not &quot;can I appeal?&quot; — it is <em>exactly how many calendar days remain today</em>.</p>

          <div className="grid lg:grid-cols-2 gap-6 mt-8">
          <div className="bg-white border border-slate-200 border-t-4 border-t-rose-500 p-6" data-testid="art-deadlines">
            <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.22em] text-rose-600 uppercase"><Clock className="h-3.5 w-3.5" /> Deadlines cannot be extended</span>
            <h3 className="font-display text-2xl text-[#29324A] mt-2">Miss the deadline, lose the review</h3>
            <p className="text-[13px] text-slate-600 mt-3 leading-relaxed">Time limits depend on the type of decision and whether you are in immigration detention. Your <strong>Home Affairs decision letter</strong> is the only safe source for your exact deadline.</p>
            <ul className="mt-4 space-y-3">
              {DEADLINES.map((d) => (
                <li key={d.k} className="text-[12.5px]"><strong className="text-[#29324A]">{d.k}:</strong> <span className="text-slate-600">{d.v}</span></li>
              ))}
            </ul>
          </div>
          <div className="bg-white border border-slate-200 border-t-4 border-t-emerald-500 p-6" data-testid="art-hardship">
            <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.22em] text-emerald-700 uppercase"><HeartHandshake className="h-3.5 w-3.5" /> Fee relief</span>
            <h3 className="font-display text-2xl text-[#29324A] mt-2">Financial hardship can halve the fee</h3>
            <p className="text-[13px] text-slate-600 mt-3 leading-relaxed">You don&apos;t automatically have to pay the full amount — a <strong>50% reduction</strong> may be granted for migration reviews on financial hardship grounds.</p>
            <ul className="mt-4 space-y-3">
              {HARDSHIP.map((d) => (
                <li key={d.k} className="text-[12.5px]"><strong className="text-[#29324A]">{d.k}:</strong> <span className="text-slate-600">{d.v}</span></li>
              ))}
            </ul>
          </div>
          </div>
        </div>
      </section>

      {/* PROTECTION + STUDENT */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-6">
            <div className="bg-[#F8F4FB] border-l-4 border-[#29324A] p-6" data-testid="art-protection">
              <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.22em] text-[#29324A] uppercase"><ShieldCheck className="h-3.5 w-3.5" /> Protection reviews work differently</span>
              <h3 className="font-display text-xl text-[#29324A] mt-2">Protection visa review fee: $2,203 → $2,293</h3>
              <p className="text-[13px] text-slate-700 leading-relaxed mt-3">Unlike most migration reviews (where the fee is paid upfront), the protection review fee is generally payable <strong>only if the application is not successful</strong>. Protection matters can be complex and may affect your safety, family and future in Australia — read the decision letter carefully and understand the specific process that applies.</p>
            </div>
            <div className="bg-[#FDF2F8] border-l-4 border-[#C43A89] p-6" data-testid="art-student">
              <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.22em] text-[#C43A89] uppercase"><GraduationCap className="h-3.5 w-3.5" /> Student refusals — big change</span>
              <h3 className="font-display text-xl text-[#29324A] mt-2">Many student reviews are now decided WITHOUT a hearing</h3>
              <p className="text-[13px] text-slate-700 leading-relaxed mt-3">In many student visa refusal reviews the Tribunal may decide the matter on the <strong>written materials alone</strong>. Don&apos;t assume you&apos;ll get a chance to explain in person later — your written submissions and documents ARE your case.</p>
              <ul className="mt-3 space-y-2">
                {STUDENT_REVIEW.map((d) => (
                  <li key={d.k} className="text-[12.5px]"><strong className="text-[#29324A]">{d.k}:</strong> <span className="text-slate-600">{d.v}</span></li>
                ))}
              </ul>
            </div>
          </div>

          {/* Step-by-step process */}
          <div className="mt-12" data-testid="art-process-steps">
            <h3 className="font-display text-2xl md:text-3xl text-[#29324A]">Step-by-step: what happens during an ART review</h3>
            <div className="mt-6 grid sm:grid-cols-2 gap-4">
              {PROCESS_STEPS.map((s, i) => (
                <div key={s.t} className="bg-white border border-slate-200 border-l-4 border-l-[#29324A] p-5">
                  <div className="flex items-center gap-3">
                    <span className="font-display text-2xl text-[#C43A89]/40 leading-none">{String(i + 1).padStart(2, "0")}</span>
                    <div className="font-display text-[16px] text-[#29324A]">{s.t}</div>
                  </div>
                  <p className="text-[12.5px] text-slate-600 leading-relaxed mt-2">{s.d}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Refusal grounds */}
          <div className="mt-12">
            <h3 className="font-display text-2xl md:text-3xl text-[#29324A]">Common refusal grounds — and what your ART submission must do</h3>
            <div className="mt-6 bg-white border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm min-w-[680px]" data-testid="art-grounds-table">
                <thead><tr className="bg-[#29324A] text-white">
                  <th className="text-left px-4 py-3.5 font-display text-[13px]">Refusal ground</th>
                  <th className="text-left px-4 py-3.5 font-display text-[13px]">What the Department usually says</th>
                  <th className="text-left px-4 py-3.5 font-display text-[13px]">What the ART submission must do</th>
                </tr></thead>
                <tbody>
                  {REFUSAL_GROUNDS.map((r, i) => (
                    <tr key={r.g} className={`${i % 2 ? "bg-[#FCFBFD]" : "bg-white"} border-t border-slate-100`}>
                      <td className="px-4 py-3.5 text-[12.5px] text-[#29324A] font-semibold whitespace-nowrap">{r.g}</td>
                      <td className="px-4 py-3.5 text-[12.5px] text-slate-600">{r.says}</td>
                      <td className="px-4 py-3.5 text-[12.5px] text-slate-700">{r.must}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Review types */}
          <div className="mt-12">
            <h3 className="font-display text-2xl md:text-3xl text-[#29324A]">Every refusal type needs a different strategy</h3>
            <p className="text-[13.5px] text-slate-600 mt-3 max-w-3xl">The fee increase affects far more than student visas. A partner refusal review is not prepared like a visitor refusal, and a nomination refusal is not like a work visa refusal — your case must answer the exact reasons in YOUR decision letter.</p>
            <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="art-review-types">
              {REVIEW_TYPES.map((r) => (
                <div key={r.t} className="bg-white border border-slate-200 hover:border-[#C43A89] transition-colors p-5">
                  <div className="font-display text-[15px] text-[#29324A]">{r.t}</div>
                  <p className="text-[12.5px] text-slate-600 leading-relaxed mt-1.5">{r.w}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Poor vs strong */}
          <div className="mt-12">
            <h3 className="font-display text-2xl md:text-3xl text-[#29324A]">Weigh the cost against the risk of a weak application</h3>
            <p className="text-[13.5px] text-slate-600 mt-3 max-w-3xl">AUD $3,727 is significant — but the bigger issue is preparation. Paying the fee without addressing the refusal reasons leads to disappointment. A review must be built around evidence, legal criteria and the decision reasons.</p>
            <div className="mt-6 bg-white border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm min-w-[560px]" data-testid="art-approach-table">
                <thead><tr className="bg-[#29324A] text-white">
                  <th className="text-left px-4 py-3 font-display text-[13px]">Poor review approach</th>
                  <th className="text-left px-4 py-3 font-display text-[13px]">Stronger review approach</th>
                </tr></thead>
                <tbody>
                  {APPROACH.map((r, i) => (
                    <tr key={r.poor} className={`${i % 2 ? "bg-[#FCFBFD]" : "bg-white"} border-t border-slate-100`}>
                      <td className="px-4 py-3 text-[12.5px] text-slate-700"><span className="inline-flex items-start gap-2"><X className="h-3.5 w-3.5 text-rose-500 shrink-0 mt-0.5" /> {r.poor}</span></td>
                      <td className="px-4 py-3 text-[12.5px] text-slate-700"><span className="inline-flex items-start gap-2"><CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0 mt-0.5" /> {r.strong}</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>

      {/* PROCESSING TIMES */}
      <section className="py-14 md:py-20 bg-[#F8F9FA]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#29324A] uppercase"><Clock className="h-3 w-3" /> Plan for the journey, not just lodgement day</span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3 max-w-3xl">How long do ART reviews take?</h2>
          <p className="text-[13.5px] text-slate-600 mt-4 max-w-3xl">Tribunal data for migration reviews finalised between <strong>1 December 2025 and 31 May 2026</strong>: half of all migration reviews finalised within <strong className="text-[#C43A89]">1 year 7 months</strong>, and 95% within <strong className="text-[#C43A89]">2 years 10 months</strong> of lodgement. These are historical figures, not promises — your case depends on category, complexity, evidence and Tribunal workload.</p>
          <div className="mt-8 bg-white border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm min-w-[560px]" data-testid="art-processing-table">
              <thead><tr className="bg-[#29324A] text-white">
                <th className="text-left px-4 py-4 font-display text-[13px]">Migration review category</th>
                <th className="text-left px-4 py-4 font-display text-[13px]">50% finalised within</th>
                <th className="text-left px-4 py-4 font-display text-[13px]">95% finalised within</th>
              </tr></thead>
              <tbody>
                {PROCESSING.map((r, i) => (
                  <tr key={r.cat} className={`${i % 2 ? "bg-[#FCFBFD]" : "bg-white"} border-t border-slate-100`}>
                    <td className="px-4 py-3.5 text-[12.5px] text-[#29324A] font-semibold">{r.cat}</td>
                    <td className="px-4 py-3.5 text-[12.5px] text-slate-700">{r.half}</td>
                    <td className="px-4 py-3.5 text-[12.5px] text-slate-700">{r.most}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* CHECKLIST */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#29324A] via-[#1B1430] to-[#29324A] text-white p-6 md:p-10" data-testid="art-checklist">
            <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase"><FileText className="h-3 w-3" /> Before you pay</span>
            <h2 className="font-display text-2xl md:text-3xl mt-3">Your 8-step checklist before paying the ART review fee</h2>
            <div className="grid sm:grid-cols-2 gap-3 mt-6">
              {CHECKLIST.map(([t, d], i) => (
                <div key={t} className="flex gap-3 bg-white/10 border border-white/15 px-4 py-3.5">
                  <span className="font-display text-xl text-[#C43A89] leading-none shrink-0">{i + 1}</span>
                  <div>
                    <div className="text-[13px] font-semibold">{t}</div>
                    <div className="text-[12px] text-slate-300 mt-0.5">{d}</div>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[11.5px] text-slate-300 mt-5">Around 1 July 2026, fee changes and payment timing can create confusion — do not wait until the last day to lodge, pay or seek advice.</p>
          </div>

          {/* Situations */}
          <div className="mt-12">
            <h3 className="font-display text-2xl md:text-3xl text-[#29324A]">When fees rise, the cost of mistakes rises too</h3>
            <p className="text-[13.5px] text-slate-600 mt-3 max-w-3xl">A missed deadline, wrong fee assumption, weak submission or unsupported document set can have serious consequences. Whatever your situation — don&apos;t panic, but act quickly and carefully:</p>
            <div className="mt-6 bg-white border border-slate-200 overflow-x-auto">
              <table className="w-full text-sm min-w-[520px]" data-testid="art-situations-table">
                <thead><tr className="bg-[#29324A] text-white">
                  <th className="text-left px-4 py-3 font-display text-[12.5px]">Your situation</th>
                  <th className="text-left px-4 py-3 font-display text-[12.5px]">What should be reviewed</th>
                </tr></thead>
                <tbody>
                  {SITUATIONS.map((r, i) => (
                    <tr key={r.s} className={`${i % 2 ? "bg-[#FCFBFD]" : "bg-white"} border-t border-slate-100`}>
                      <td className="px-4 py-3 text-[12.5px] text-[#29324A] font-semibold">{r.s}</td>
                      <td className="px-4 py-3 text-[12.5px] text-slate-600">{r.r}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-[13px] text-slate-600 mt-4 max-w-3xl">A well-prepared review does not guarantee success, but it lets you present your case properly. The aim is to <strong className="text-[#29324A]">address the real concerns in the decision</strong> — not simply repeat the original application.</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-20 bg-[#F8F9FA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <span className="inline-flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] text-[#C43A89] uppercase"><Info className="h-3 w-3" /> Frequently asked questions</span>
          <h2 className="font-display text-3xl md:text-4xl text-[#29324A] mt-3">ART review — your questions answered</h2>
          <div className="mt-8 space-y-3" data-testid="art-faq">
            {FAQS.map((f, i) => <Faq key={i} q={f.q} a={f.a} idx={i} />)}
          </div>
          <p className="mt-8 text-[11.5px] text-slate-500 leading-relaxed bg-white border border-slate-200 p-4">
            This page provides general information based on the 1 July 2026 ART fee update and Tribunal-published processing data. It is not legal advice and does not guarantee any review outcome. Fees, deadlines and Tribunal procedures can change — always confirm your review rights and deadline from your Home Affairs decision letter and obtain personalised advice from a Registered Migration Agent before lodging.
          </p>
        </div>
      </section>

      <CTASection
        title="Received a visa refusal or cancellation?"
        subtitle="Deadlines are strict and cannot be extended. Our Registered Migration Agents will review your decision letter, confirm your review rights and build a case-specific ART strategy — before your time runs out."
      />
      <Disclaimer />
    </div>
  );
}

function Faq({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white border border-slate-200">
      <button type="button" data-testid={`art-faq-${idx}`} onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left">
        <span className="font-display text-[15px] text-[#29324A]">{q}</span>
        <ChevronDown className={`h-4 w-4 text-[#C43A89] shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <p className="px-5 pb-5 text-[13px] text-slate-600 leading-relaxed">{a}</p>}
    </div>
  );
}
